import { CreatableCustomer } from './customer';
import { Article, Variant, Articles } from './products';
import { CatalogFormReplies } from './forms';

import { fetchJson } from './backend';
import { Backend } from '.';

export interface VatRates {
  [rate: string]: string;
}

export interface Version {
  id: number;
  title: string;
  full_title: string;
  price: number;
  quantity: number;
}

export interface CartArticle extends Article {
  location: string;
}

export interface CartVariant extends Variant {
  article: CartArticle;
  title: string;
  full_title: string;
  is_fee: boolean;
  is_option: boolean;
  location?: string;
  valid_start_on?: Date;
  versions: Version[];
}

export interface CartSeat {
  handicap: boolean;
  id: number;
  position: string;
  variant: CartVariant;
}

export interface CartPackageItem {
  quantity: number;
  seats: CartSeat[];
  variant: CartVariant;
}

export interface CartItem {
  id: number;
  quantity: number;
  replies: CatalogFormReplies;
  seats: CartSeat[];
  total_price: number;
  variant: CartVariant;
  /**
   * used in the toaster for packages
   */
  cart_items?: CartPackageItem[];
  voucher_swaps?: VoucherSwap[];
}

export type CartItems = CartItem[];

export interface VoucherSwap {
  token: string;
  url: string;
  cart: boolean;
  sale: boolean;
  // api path to delete the swap
  revoke_path: string;
  status: 'new' | 'carted' | 'sold' | 'paid';
}

export const countriesThatRequireState = ['US', 'CA', 'AU'];

interface AnyCartMethod {
  title: string;
  description: string;
  icon: string;
  id: number;
  price: number;
  sid: string;
}

export interface ShippingMethod extends AnyCartMethod {
  sid: 'online' | 'postal' | 'postal2' | 'postal3' | 'postal4';
}

export type ShippingMethods = ShippingMethod[];

export interface PaymentMethod extends AnyCartMethod {}

export type PaymentMethods = PaymentMethod[];

export interface ShippingAttributes {
  shipping_address_addition: string;
  shipping_city: string;
  shipping_companyname: string;
  shipping_country: string;
  shipping_firstname: string;
  shipping_lastname: string;
  shipping_method_id?: number;
  shipping_method_sid?: string;
  shipping_state?: string;
  shipping_street: string;
  shipping_zip_code: string;
  use_shipping_address: boolean;
}

export interface ShippingInformation extends CreatableCustomer, ShippingAttributes {
  use_customer_address?: boolean;
}

export interface PaymentAttributes {
  account_owner?: string;
  bic?: string;
  iban?: string;
  street_address?: string;
  zip_city?: string;
}

export interface PaymentInformation {
  payment_method_id?: number;
  payment_method_sid?: string;
  payment_attributes?: PaymentAttributes;
  payment_method_title?: string;
}

export interface Coupon {
  title: string;
  token: string;
  value: number;
}

export interface TncInfo {
  id: number;
  title: string;
}

export interface Cart extends ShippingInformation, PaymentInformation {
  available_payment_methods: PaymentMethods;
  available_shipping_methods: ShippingMethods;
  cancelled: boolean;
  /**
   * If this is set, the cart is being converted and locked
   */
  cart_token?: string;
  created_at?: Date;
  channel_id: number;
  coupons: Coupon[];
  customer_id: number;
  expires_at: Date;
  expires_in: number;
  extra_tncs: TncInfo[];
  id: number;
  items: CartItems;
  newsletter: {
    /**
     * Defines whether the newsletter question should be asked or not
     */
    ask: boolean;
    /**
     * Override the system message with a message provided by the admin
     */
    question?: string;
    /**
     * defines if the checkbox should be checked by default
     */
    default: boolean;
  };
  non_customer: boolean;
  position: string;
  price_after_coupons: number;
  promo_code_id: number;
  status: string;
  title: string;
  total_value: number;
  type: string;
  updated_at?: Date;
  vat_rates: VatRates;
  errors: {
    [key in keyof (ShippingInformation | PaymentInformation)]: string;
  };
  no_checkout_message?: string;
  message?: string;
  valid: boolean;
  validations?: CartValidations;
}

export interface CartValidations {
  email?: CartValidation;
  address?: CartValidation;
  phone?: CartValidation;
}

export interface CartValidation {
  /**
   * Show or hide this field
   */
  ask: boolean;
  /**
   * Input is required
   */
  required: boolean;
}

export interface PackageVariantsMap {
  /**
   * Group ID to Variant ID mapping
   */
  [groupId: number]: number;
}

export async function getCart() {
  return Backend.get<Cart>('/shopping/cart');
}

export async function getCartOptions() {
  return Backend.get<Articles>('/shopping/cart/options');
}

export async function updateCartShippingInformation(
  shippingInformation: ShippingInformation,
  options: RequestInit = {}
) {
  return Backend.patch<Cart>('/shopping/cart', {
    ...options,
    body: JSON.stringify(shippingInformation),
  });
}

export async function updateCartPaymentInformation(paymentInformation: PaymentInformation) {
  return Backend.patch<Cart>('/shopping/cart', {
    body: JSON.stringify(paymentInformation),
  });
}

export interface CheckoutStatus {
  status: "OK",
  cart_token: string;
}

export async function checkoutCart(allowPromotion: boolean) {
  return Backend.post<CheckoutStatus>(`/shopping/cart/checkouts?allow_promotion=${encodeURIComponent(allowPromotion)}`);
}

export async function addItemToCart(variantId: number, quantity: number) {
  return Backend.post<Cart>('/shopping/cart/items', {
    body: JSON.stringify({
      variant_id: variantId,
      quantity,
    }),
  });
}

export async function addPackageItemToCart(variant_id: number, quantity: number, variants_map: PackageVariantsMap) {
  return Backend.post<Cart>('/shopping/cart/items', {
    body: JSON.stringify({
      variant_id,
      quantity,
      variants_map,
    }),
  });
}

/**
 * @description Schickt einen handle an den Server um 'geheimes' Produkt in den Warenkorb zu legen
 * @returns Entweder 200 mit einem neuen Warenkorb, oder 422 ohne 'richtigen' Fehler
 */
export async function applyHandleToCart(password: string, variantId: number) {
  return Backend.post<Cart>(`/shopping/cart/apply_handle?token=${password}&product_id=${variantId}`);
}

export async function modifyItemInCart(cartItemId: number, quantity: number) {
  return Backend.put<Cart>(`/shopping/cart/items/${cartItemId}`, {
    body: JSON.stringify({
      quantity,
    }),
  });
}

export async function removeItemFromCart(cartItemId: number) {
  return Backend.del<Cart>(`/shopping/cart/items/${cartItemId}`);
}

/**
 * Replies that are omitted will not be updated, since it's a PATCH
 * @param cartItemId number
 * @param replies CatalogFormReplies
 */
export async function updateRepliesForCartItem(cartItemId: number, replies: CatalogFormReplies) {
  return Backend.patch<Cart>(`/shopping/cart/items/${cartItemId}/replies`, {
    body: JSON.stringify({
      replies,
    }),
  });
}

export async function addCoupon(token: string) {
  return Backend.post<Cart>('/shopping/cart/coupons', {
    body: JSON.stringify({
      token,
    }),
  });
}

export async function addSeatItemToCart(seatId: number) {
  return Backend.post<Cart>('/shopping/cart/items/seats', {
    body: JSON.stringify({
      id: seatId,
    }),
  });
}

export async function removeSeatItemFromCart(seatId: number) {
  return Backend.del<Cart>(`/shopping/cart/items/seats/${seatId}`);
}

// To change a variant of a Seat already in Cart, send an Array of all Seats in the cart.
// [{"variantId":794, "seatId":232}, ...] with the variantId you want changed.
export async function modifySeatVariantInCart(cartItemId: number, seats: { variantId: number; seatId: number }[]) {
  return Backend.put<Cart>(`/shopping/cart/items/${cartItemId}/change_variant`, {
    body: JSON.stringify({
      seats,
    }),
  });
}

export async function revokeVoucherSwap(path: string) {
  return Backend.delOldApi<Cart>(path, {});
}

export interface PaymentData {
  public_key: string;
  session_id: string;
}

export enum ConversionStatusType {
  PENDING = 'PENDING',
  DONE = 'DONE',
}

export interface PendingConversion {
  status: ConversionStatusType.PENDING;
}

export interface SuccessfulConversion {
  status: ConversionStatusType.DONE;
  goto: 'thankyou';

  order_no: string;
  order_id: number;
  simple_token: string;
}

export interface StripeConversion {
  status: ConversionStatusType.DONE;
  goto: 'stripe';

  payment_data: PaymentData;
}

export interface URLConversion {
  status: ConversionStatusType.DONE;
  goto: string;
}

export interface ErrorConversion {
  error?: string;
}

export type SuccessfulConversionStatuses = PendingConversion | SuccessfulConversion | StripeConversion | URLConversion;

export type ConversionStatus = SuccessfulConversionStatuses | ErrorConversion;

export function getConversionStatus(cartToken: string) {
  return Backend.get<ConversionStatus>(`/shopping/cart/${encodeURIComponent(cartToken)}/status`);
}
